import React from 'react';
import { Card, Badge, Alert } from 'react-bootstrap';
import { PassScanResult, AddonScanResult, ActivityScanResult, SeatInfo } from '../../types';
import './ScanResultCard.scss';

interface ScanResultCardProps {
  result: PassScanResult | AddonScanResult | ActivityScanResult;
  type?: 'pass' | 'addon' | 'activity';
}

const ScanResultCard: React.FC<ScanResultCardProps> = ({ result, type = 'pass' }) => {
  const isSuccess = result.success;
  const isPass = type === 'pass' || 'category' in result;
  const isAddon = type === 'addon' || ('quantity' in result && !('category' in result));
  const isActivity = type === 'activity' || ('dues' in result && 'time' in result);

  const passResult = isPass ? (result as PassScanResult) : null;
  const addonResult = isAddon ? (result as AddonScanResult) : null;
  const activityResult = isActivity ? (result as ActivityScanResult) : null;

  return (
    <Card className={`scan-result-card ${isSuccess ? 'success' : 'error'}`}>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start mb-3">
          <h5 className="mb-0">
            {isSuccess ? (
              <Badge bg="success" className="me-2">
                ✓ Success
              </Badge>
            ) : (
              <Badge bg="danger" className="me-2">
                ✗ Failed
              </Badge>
            )}
            {passResult?.name || addonResult?.name || activityResult?.name || 'Scan Result'}
          </h5>
        </div>

        <Alert variant={isSuccess ? 'success' : 'warning'} className="mb-3">
          {result.message}
        </Alert>

        {passResult && (
          <div className="pass-details">
            <div className="detail-row">
              <strong>Category:</strong> {passResult.category}
            </div>
            <div className="detail-row">
              <strong>Email:</strong> {passResult.email}
            </div>
            <div className="detail-row">
              <strong>Phone:</strong> {passResult.phone}
            </div>
            {passResult.scanned_by && (
              <div className="detail-row">
                <strong>Scanned by:</strong> {passResult.scanned_by}
              </div>
            )}
            {passResult.scanned_on && (
              <div className="detail-row">
                <strong>Scanned on:</strong> {passResult.scanned_on}
              </div>
            )}
            {passResult.photo_url && (
              <div className="detail-row">
                <img src={passResult.photo_url} alt="Pass holder" className="pass-photo" />
              </div>
            )}
            {passResult.seat_info && (
              <div className="detail-row">
                <strong>Seat:</strong> {passResult.seat_info.row} - {passResult.seat_info.number}
              </div>
            )}
            {passResult.additional_fields && Object.keys(passResult.additional_fields).length > 0 && (
              <div className="detail-row">
                <strong>Additional Fields:</strong>
                <ul className="additional-fields">
                  {Object.entries(passResult.additional_fields).map(([key, value]) => (
                    <li key={key}>
                      <strong>{key}:</strong> {String(value)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {passResult.pass_history && passResult.pass_history.length > 0 && (
              <div className="detail-row">
                <strong>Previous Scans:</strong>
                <ul className="pass-history">
                  {passResult.pass_history.map((history, index) => (
                    <li key={index}>
                      {history.category} - {history.scanned_on} by {history.scanned_by}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {addonResult && (
          <div className="addon-details">
            <div className="detail-row">
              <strong>Addon:</strong> {addonResult.name}
            </div>
            <div className="detail-row">
              <strong>Quantity:</strong> {addonResult.quantity}
            </div>
            {addonResult.purchaser_name && (
              <div className="detail-row">
                <strong>Purchaser:</strong> {addonResult.purchaser_name}
              </div>
            )}
            {addonResult.purchaser_email && (
              <div className="detail-row">
                <strong>Email:</strong> {addonResult.purchaser_email}
              </div>
            )}
            {addonResult.purchaser_phone && (
              <div className="detail-row">
                <strong>Phone:</strong> {addonResult.purchaser_phone}
              </div>
            )}
            {addonResult.additional_data && Object.keys(addonResult.additional_data).length > 0 && (
              <div className="detail-row">
                <strong>Additional Data:</strong>
                <ul className="additional-fields">
                  {Object.entries(addonResult.additional_data).map(([key, value]) => (
                    <li key={key}>
                      <strong>{key}:</strong> {String(value)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {activityResult && (
          <div className="activity-details">
            <div className="detail-row">
              <strong>Category:</strong> {activityResult.category}
            </div>
            <div className="detail-row">
              <strong>Email:</strong> {activityResult.email}
            </div>
            <div className="detail-row">
              <strong>Phone:</strong> {activityResult.phone}
            </div>
            {activityResult.date && (
              <div className="detail-row">
                <strong>Date:</strong> {new Date(activityResult.date).toLocaleDateString()}
              </div>
            )}
            {activityResult.time && (
              <div className="detail-row">
                <strong>Time:</strong> {new Date(activityResult.time).toLocaleTimeString()}
              </div>
            )}
            {activityResult.dues && (
              <div className="detail-row">
                <strong>Dues:</strong> {activityResult.dues}
              </div>
            )}
            {activityResult.scanned_by && (
              <div className="detail-row">
                <strong>Scanned by:</strong> {activityResult.scanned_by}
              </div>
            )}
            {activityResult.scanned_on && (
              <div className="detail-row">
                <strong>Scanned on:</strong> {activityResult.scanned_on}
              </div>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default ScanResultCard;

